/**
 * Light, dark, or whatever the machine says. A per-device preference, kept in
 * localStorage because nothing on the server needs to agree with it.
 *
 * Auto is the *absence* of `data-theme`, so choosing it removes the attribute
 * rather than writing a third value.
 *
 * The storage key belongs to the app, not to this package: changing an app's
 * key forgets every saved choice, so each app binds its own once, in its own
 * `lib/theme.ts`. That app's `public/theme-boot.js` reads the same key before
 * the first paint; keep the two in step.
 */

import { useCallback, useState } from "react";

export type Theme = "auto" | "light" | "dark";

export interface ThemeStore {
  readTheme(): Theme;
  applyTheme(theme: Theme): void;
  useTheme(): [Theme, (theme: Theme) => void];
}

export function themeStore(key: string): ThemeStore {
  function readTheme(): Theme {
    try {
      const value = window.localStorage.getItem(key);
      return value === "light" || value === "dark" ? value : "auto";
    } catch {
      return "auto";
    }
  }

  function applyTheme(theme: Theme): void {
    const root = document.documentElement;
    if (theme === "auto") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
    try {
      if (theme === "auto") window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, theme);
    } catch {
      // It applies for this visit either way.
    }
  }

  function useTheme(): [Theme, (theme: Theme) => void] {
    const [theme, setTheme] = useState<Theme>(readTheme);
    const choose = useCallback((next: Theme) => {
      applyTheme(next);
      setTheme(next);
    }, []);
    return [theme, choose];
  }

  return { readTheme, applyTheme, useTheme };
}
